import Select from '@/components/elements/forms/Select/Select';
import Icon from '@/components/icons/Icon';
import type { ReactHookFormRegister } from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import { classnames } from '@/helpers';
import { LegacyRef, SelectHTMLAttributes, forwardRef, useId } from 'react';

type GenericListInputProps = {
  error?: string;
  label?: string;
  leftIcon?: React.ReactNode;
  children: React.ReactNode;
  leftPadding?: boolean;
  floatStyle?: boolean;
};

type ListInputProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> &
  GenericListInputProps &
  ReactHookFormRegister<any>;

const ListInputSelect = forwardRef(
  (
    { error, label, leftIcon, children, disabled, leftPadding = true, floatStyle = false, ...props }: ListInputProps,
    ref: LegacyRef<HTMLSelectElement>,
  ) => {
    // Minted rather than accepted as a prop: callers reuse field ids across surfaces, and
    // `htmlFor` binds to whichever element carries that id first in tree order.
    const inputId = useId();
    if (floatStyle) {
      return (
        <div className={classnames(leftPadding && 'pl-lg', 'pr-lg flex flex-col', disabled && 'opacity-40')}>
          <div
            className={classnames(
              'px-md relative flex items-center gap-2 rounded-sm border py-2',
              error ? 'border-danger-500' : 'border-black-100',
              'float-group',
            )}>
            {leftIcon && <div className="w-7">{leftIcon}</div>}
            <div className="relative w-full">
              {label && (
                <label
                  htmlFor={inputId}
                  className={classnames(
                    'text-black-600 text-l absolute left-0 top-2 block',
                    disabled && 'text-black-400',
                  )}>
                  {label}
                </label>
              )}
              <Select
                iconVariant="chevron-down"
                className="!py-0"
                id={inputId}
                {...props}
                disabled={disabled}
                innerRef={ref}>
                {children}
              </Select>
            </div>
          </div>
          {error && <div className="text-fg_negative text-s mt-xxs flex justify-start">{error}</div>}
        </div>
      );
    }

    return (
      <div className={classnames(`${leftPadding ? 'pl-lg' : ''} flex flex-col`, disabled && 'opacity-40')}>
        {label && (
          <label htmlFor={inputId} className="text-black-900 pt-md text-m">
            {label}
          </label>
        )}
        <div className={`pr-lg relative flex border-b ${error ? 'border-danger-500' : 'border-black-100'}`}>
          {leftIcon && <div className="pr-md mt-md">{leftIcon}</div>}
          <Select id={inputId} {...props} disabled={disabled} innerRef={ref}>
            {children}
          </Select>
          {error && (
            <div className="mt-md">
              <Icon variant="warning" color="danger-500" />
            </div>
          )}
        </div>
        {error && <div className="text-danger text-xxs mt-xxs flex justify-start">{error}</div>}
      </div>
    );
  },
);

export default ListInputSelect;
